import { computed, ref } from 'vue'
import { navGroups } from '../navigation'

const RECENT_KEY = 'church_nav_recent'
const PINNED_KEY = 'church_nav_pinned'
const MAX_RECENT = 3

function read(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]')
    return Array.isArray(value) ? value : []
  } catch {
    return []
  }
}

const recentRoutes = ref(read(RECENT_KEY))
const pinnedRoutes = ref(read(PINNED_KEY))

function allItems() {
  return navGroups().flatMap((group) => group.items)
}

function resolve(routes) {
  const byRoute = new Map(allItems().map((item) => [item.route, item]))
  return routes.map((route) => byRoute.get(route)).filter(Boolean)
}

function persist(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage unavailable (private mode/quota): recents and pins just won't persist.
  }
}

export function useRecentPages() {
  const pinned = computed(() => resolve(pinnedRoutes.value))
  const recent = computed(() =>
    resolve(recentRoutes.value.filter((route) => !pinnedRoutes.value.includes(route))).slice(0, MAX_RECENT),
  )
  const pinnedSet = computed(() => new Set(pinnedRoutes.value))

  function record(route) {
    if (!route || route === '/') return
    // Record the nav destination, not a deep detail URL that can't be resolved back.
    const exact = allItems().find((item) => item.route === route)
    const target = exact || allItems().find((item) => route.startsWith(`${item.route}/`))
    if (!target) return
    recentRoutes.value = [target.route, ...recentRoutes.value.filter((r) => r !== target.route)].slice(
      0,
      MAX_RECENT * 2,
    )
    persist(RECENT_KEY, recentRoutes.value)
  }

  function togglePin(route) {
    if (pinnedSet.value.has(route)) {
      pinnedRoutes.value = pinnedRoutes.value.filter((r) => r !== route)
    } else {
      pinnedRoutes.value = [route, ...pinnedRoutes.value].slice(0, 6)
    }
    persist(PINNED_KEY, pinnedRoutes.value)
  }

  function isPinned(route) {
    return pinnedSet.value.has(route)
  }

  return { pinned, recent, record, togglePin, isPinned }
}
